import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Fade,
  Chip
} from '@mui/material';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import HomeIcon from '@mui/icons-material/Home';
import ShieldIcon from '@mui/icons-material/Shield';

const NoAuthAccess = ({ requestedPath }) => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        marginTop: '20px',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in timeout={800}>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
            {/* Animated Icon Container */}
            <Box
              sx={{
                width: 120,
                height: 120,
                margin: '0 auto 32px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                animation: 'pulse 2s infinite',
                boxShadow: '0 10px 30px rgba(25, 118, 210, 0.4)',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                }
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  border: '3px solid rgba(102, 126, 234, 0.3)',
                  animation: 'ripple 2s infinite',
                  '@keyframes ripple': {
                    '0%': { transform: 'scale(1)', opacity: 1 },
                    '100%': { transform: 'scale(1.4)', opacity: 0 },
                  }
                }}
              />
              <LockPersonIcon sx={{ fontSize: 60, color: 'white' }} />
            </Box>

            {/* Status Chip */}
            <Chip
              icon={<ShieldIcon sx={{ fontSize: 18, color: "blue" }} />}
              label="Access Restricted"
              color="error"
              sx={{
                mb: 3,
                fontWeight: 600,
                fontSize: '0.875rem',
                height: 32,
                '& .MuiChip-icon': {
                  color: 'white'
                }
              }}
            />

            {/* Title */}
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem' }
              }}
            >
              Unauthorized Access
            </Typography>

            {/* Description */}
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                mb: 3,
                fontWeight: 400,
                fontSize: '1.1rem',
                lineHeight: 1.6
              }}
            >
              You don't have permission to view this page.
              <br />
            </Typography>

            {/* Requested Path */}
            {requestedPath && (
              <Box
                sx={{
                  mb: 3,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  backgroundColor: 'rgba(102, 126, 234, 0.06)',
                  borderRadius: 1.5,
                  border: '1px solid rgba(102, 126, 234, 0.15)',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ 
                    color: 'text.secondary',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    letterSpacing: '0.5px'
                  }}
                >
                  Path:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                >
                  {requestedPath}
                </Typography>
              </Box>
            )}

            {/* Go to Home Button */}
            <Button
              variant="contained"
              size="large"
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
              sx={{
                py: 1.75,
                px: 5,
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 700,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 15px 35px rgba(102, 126, 234, 0.4)',
                  background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                }
              }}
            >
              Go to Home
            </Button>

            {/* Footer */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 5,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider',
                fontSize: '0.875rem',
                opacity: 0.8
              }}
            >
              Need help? Contact your system administrator
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default NoAuthAccess;